/**
 * What a typed amount cell means, in cents.
 *
 * Her sheet does arithmetic in the amount column and always has -- `=(3.19+1)*1.06` for lunch plus
 * tax, `=130-15` for a budget she has already spent part of. Those are not decoration: the number
 * she wants is the one she can only write as a sum, and without this she would be reaching for a
 * calculator and typing the answer, which is exactly the step a spreadsheet exists to remove.
 *
 * So the grammar is small and deliberate rather than a general expression language: numbers, the
 * four operators, parentheses, a unary sign. No cell references -- a ledger row's amount is its
 * own, and a reference would need a recalculation graph to go with it.
 *
 * Parsed by hand rather than handed to `eval`, which would run whatever was pasted into the cell.
 */

const NUMBER = /^\d+(\.\d+)?|^\.\d+/;

function tokenize(text) {
	const tokens = [];
	let rest = text;
	while(rest.length) {
		const character = rest[0];
		if(character === ' ') {
			rest = rest.slice(1);
			continue;
		}
		if('+-*/()'.includes(character)) {
			tokens.push(character);
			rest = rest.slice(1);
			continue;
		}
		const number = NUMBER.exec(rest);
		if(!number) {
			return null;
		}
		tokens.push(Number.parseFloat(number[0]));
		rest = rest.slice(number[0].length);
	}
	return tokens;
}

/**
 * A recursive descent pass over the token list. Each function returns the value it consumed and
 * moves `position`, or null if it could not, which unwinds the whole parse to null.
 */
function parse(tokens) {
	let position = 0;

	function peek() {
		return tokens[position];
	}

	function factor() {
		const token = peek();
		if(token === '-' || token === '+') {
			position++;
			const value = factor();
			return value === null ? null : (token === '-' ? -value : value);
		}
		if(token === '(') {
			position++;
			const value = expression();
			if(value === null || peek() !== ')') {
				return null;
			}
			position++;
			return value;
		}
		if(typeof token === 'number') {
			position++;
			return token;
		}
		return null;
	}

	function term() {
		let value = factor();
		if(value === null) {
			return null;
		}
		while(peek() === '*' || peek() === '/') {
			const operator = peek();
			position++;
			const right = factor();
			if(right === null || (operator === '/' && right === 0)) {
				return null;
			}
			value = operator === '*' ? value * right : value / right;
		}
		return value;
	}

	function expression() {
		let value = term();
		if(value === null) {
			return null;
		}
		while(peek() === '+' || peek() === '-') {
			const operator = peek();
			position++;
			const right = term();
			if(right === null) {
				return null;
			}
			value = operator === '+' ? value + right : value - right;
		}
		return value;
	}

	const value = expression();
	return (value === null || position !== tokens.length) ? null : value;
}

/**
 * Cents for what was typed into an amount cell.
 *
 * @param text {string} as typed -- a leading `=`, `$` and thousands separators are all allowed
 * @returns {number|null} cents, or null if it does not read as an amount at all
 */
export default function amountExpression(text) {
	if(text === null || text === undefined) {
		return null;
	}
	const cleaned = String(text).trim().replace(/^=/, '').replace(/[$,]/g, '').trim();
	if(cleaned === '') {
		return null;
	}
	const tokens = tokenize(cleaned);
	if(tokens === null || !tokens.length) {
		return null;
	}
	const value = parse(tokens);
	if(value === null || !Number.isFinite(value)) {
		return null;
	}
	// Rounded to the cent here rather than left as a float: the app stores cents as integers, and
	// 1.06 tax on $4.19 is 444.14 of them.
	return Math.round(value * 100);
}
